import {
  HStack,
  Icon,
  Box,
  Text,
  Separator,
  Button,
  AbsoluteCenter,
  IconButton,
} from "@chakra-ui/react";
import { useDispatch, useSelector } from "react-redux";
import { IoAddSharp } from "react-icons/io5";
import { X } from "lucide-react";
import { useState, useMemo } from "react";
import _ from "lodash";

import {
  updateLevel,
  updateLocalSubscription,
  setActiveReportTypeId,
} from "../../../../../store/actions/subscriptionsActions";

import { LEVELS } from "../../../../../store/constants";
import {
  ARRIVAL_RATES_PER_SERVICE_COLUMNS,
  ARRIVAL_RATES_PER_SERVICE_COLUMN_DEFINITIONS,
  ARRIVAL_RATES_SUMMARY_COLUMNS,
  ARRIVAL_RATES_SUMMARY_COLUMN_DEFINITIONS,
  AGENT_TICKETS_PER_SERVICE_COLUMN_DEFINITIONS,
  AGENT_TICKETS_PER_SERVICE_COLUMNS,
  CUSTOMERS_IN_BRANCH_COLUMNS,
  CUSTOMERS_IN_BRANCH_COLUMN_DEFINITIONS,
  DEFAULT_RENDER_CONFIG,
  SERVICE_TIME_PER_SERVICE_COLUMNS,
  SERVICE_TIME_PER_SERVICE_COLUMN_DEFINITIONS,
  TICKET_SUMMARY_COLUMNS,
  TICKET_SUMMARY_COLUMN_DEFINITIONS,
  TICKETS_PER_SERVICE_COLUMNS,
  TICKETS_PER_SERVICE_COLUMN_DEFINITIONS,
  WAITING_TIME_SUMMARY_COLUMNS,
  WAITING_TIME_SUMMARY_COLUMN_DEFINITIONS,
  WAITING_TIME_PER_SERVICE_COLUMNS,
  WAITING_TIME_PER_SERVICE_COLUMN_DEFINITIONS,
  getMergeRenderConfigStateItems,
} from "../../../../../store/renderConfig";
import { REPORT_TYPE_IDS } from "../../../../../store/constants";
import Select from "../../../../averly/Select";

const formatReportTypeName = (name) =>
  name
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());

const AGENT_TICKETS_REPORT_TYPES = new Set([
  REPORT_TYPE_IDS.monthly_branch_agent_tickets_per_service,
  REPORT_TYPE_IDS.weekly_branch_agent_tickets_per_service,
  REPORT_TYPE_IDS.daily_branch_agent_tickets_per_service,
  REPORT_TYPE_IDS.monthly_branch_tickets_per_agent,
  REPORT_TYPE_IDS.weekly_branch_tickets_per_agent,
  REPORT_TYPE_IDS.daily_branch_tickets_per_agent,
]);

const NO_CHART_AGENT_TICKETS_REPORT_TYPES = new Set([
  REPORT_TYPE_IDS.monthly_branch_agent_tickets_per_service,
  REPORT_TYPE_IDS.weekly_branch_agent_tickets_per_service,
  REPORT_TYPE_IDS.daily_branch_agent_tickets_per_service,
]);

const TICKETS_PER_SERVICE_REPORT_TYPES = new Set([
  REPORT_TYPE_IDS.monthly_branch_tickets_per_service,
  REPORT_TYPE_IDS.weekly_branch_tickets_per_service,
  REPORT_TYPE_IDS.daily_branch_tickets_per_service,
  REPORT_TYPE_IDS.monthly_organization_tickets_per_service,
  REPORT_TYPE_IDS.weekly_organization_tickets_per_service,
  REPORT_TYPE_IDS.daily_organization_tickets_per_service,
]);

const SERVICE_TIME_PER_SERVICE_REPORT_TYPES = new Set([
  REPORT_TYPE_IDS.monthly_branch_service_time_per_service,
  REPORT_TYPE_IDS.weekly_branch_service_time_per_service,
  REPORT_TYPE_IDS.daily_branch_service_time_per_service,
  REPORT_TYPE_IDS.monthly_organization_service_time_per_service,
  REPORT_TYPE_IDS.weekly_organization_service_time_per_service,
  REPORT_TYPE_IDS.daily_organization_service_time_per_service,
]);

const WAITING_TIME_PER_SERVICE_REPORT_TYPES = new Set([
  REPORT_TYPE_IDS.monthly_branch_waiting_time_per_service,
  REPORT_TYPE_IDS.weekly_branch_waiting_time_per_service,
  REPORT_TYPE_IDS.daily_branch_waiting_time_per_service,
  REPORT_TYPE_IDS.monthly_organization_waiting_time_per_service,
  REPORT_TYPE_IDS.weekly_organization_waiting_time_per_service,
  REPORT_TYPE_IDS.daily_organization_waiting_time_per_service,
]);

const ARRIVAL_RATES_PER_SERVICE_REPORT_TYPES = new Set([
  REPORT_TYPE_IDS.monthly_branch_arrival_rates_per_service,
  REPORT_TYPE_IDS.weekly_branch_arrival_rates_per_service,
  REPORT_TYPE_IDS.daily_branch_arrival_rates_per_service,
  REPORT_TYPE_IDS.monthly_organization_arrival_rates_per_service,
  REPORT_TYPE_IDS.weekly_organization_arrival_rates_per_service,
  REPORT_TYPE_IDS.daily_organization_arrival_rates_per_service,
]);

const ARRIVAL_RATES_SUMMARY_REPORT_TYPES = new Set([
  REPORT_TYPE_IDS.monthly_branch_arrival_rates_per_day,
  REPORT_TYPE_IDS.weekly_branch_arrival_rates_per_day,
  REPORT_TYPE_IDS.monthly_organization_arrival_rates_per_day,
  REPORT_TYPE_IDS.weekly_organization_arrival_rates_per_day,
  REPORT_TYPE_IDS.monthly_branch_arrival_rates_per_hour,
  REPORT_TYPE_IDS.weekly_branch_arrival_rates_per_hour,
  REPORT_TYPE_IDS.daily_branch_arrival_rates_per_hour,
  REPORT_TYPE_IDS.monthly_organization_arrival_rates_per_hour,
  REPORT_TYPE_IDS.weekly_organization_arrival_rates_per_hour,
  REPORT_TYPE_IDS.daily_organization_arrival_rates_per_hour,
]);

const TICKET_SUMMARY_REPORT_TYPES = new Set([
  REPORT_TYPE_IDS.monthly_organization_tickets,
  REPORT_TYPE_IDS.weekly_organization_tickets,
  REPORT_TYPE_IDS.daily_organization_tickets,
  REPORT_TYPE_IDS.monthly_organization_tickets_per_branch,
  REPORT_TYPE_IDS.weekly_organization_tickets_per_branch,
  REPORT_TYPE_IDS.daily_organization_tickets_per_branch,
]);

const WAITING_TIME_SUMMARY_REPORT_TYPES = new Set([
  REPORT_TYPE_IDS.monthly_organization_waiting_time,
  REPORT_TYPE_IDS.weekly_organization_waiting_time,
  REPORT_TYPE_IDS.daily_organization_waiting_time,
]);

const CUSTOMERS_IN_BRANCH_REPORT_TYPES = new Set([
  REPORT_TYPE_IDS.monthly_branch_customers_in_branch_per_hour,
  REPORT_TYPE_IDS.weekly_branch_customers_in_branch_per_hour,
  REPORT_TYPE_IDS.daily_branch_customers_in_branch_per_hour,
]);

const getReportColumnOptions = (reportTypeId) => {
  if (AGENT_TICKETS_REPORT_TYPES.has(reportTypeId)) {
    return {
      columns: AGENT_TICKETS_PER_SERVICE_COLUMNS,
      columnDefinitions: AGENT_TICKETS_PER_SERVICE_COLUMN_DEFINITIONS,
    };
  }
  if (TICKETS_PER_SERVICE_REPORT_TYPES.has(reportTypeId)) {
    return {
      columns: TICKETS_PER_SERVICE_COLUMNS,
      columnDefinitions: TICKETS_PER_SERVICE_COLUMN_DEFINITIONS,
    };
  }
  if (SERVICE_TIME_PER_SERVICE_REPORT_TYPES.has(reportTypeId)) {
    return {
      columns: SERVICE_TIME_PER_SERVICE_COLUMNS,
      columnDefinitions: SERVICE_TIME_PER_SERVICE_COLUMN_DEFINITIONS,
    };
  }
  if (WAITING_TIME_PER_SERVICE_REPORT_TYPES.has(reportTypeId)) {
    return {
      columns: WAITING_TIME_PER_SERVICE_COLUMNS,
      columnDefinitions: WAITING_TIME_PER_SERVICE_COLUMN_DEFINITIONS,
    };
  }
  if (ARRIVAL_RATES_PER_SERVICE_REPORT_TYPES.has(reportTypeId)) {
    return {
      columns: ARRIVAL_RATES_PER_SERVICE_COLUMNS,
      columnDefinitions: ARRIVAL_RATES_PER_SERVICE_COLUMN_DEFINITIONS,
    };
  }
  if (ARRIVAL_RATES_SUMMARY_REPORT_TYPES.has(reportTypeId)) {
    return {
      columns: ARRIVAL_RATES_SUMMARY_COLUMNS,
      columnDefinitions: ARRIVAL_RATES_SUMMARY_COLUMN_DEFINITIONS,
    };
  }
  if (TICKET_SUMMARY_REPORT_TYPES.has(reportTypeId)) {
    return {
      columns: TICKET_SUMMARY_COLUMNS,
      columnDefinitions: TICKET_SUMMARY_COLUMN_DEFINITIONS,
    };
  }
  if (WAITING_TIME_SUMMARY_REPORT_TYPES.has(reportTypeId)) {
    return {
      columns: WAITING_TIME_SUMMARY_COLUMNS,
      columnDefinitions: WAITING_TIME_SUMMARY_COLUMN_DEFINITIONS,
    };
  }
  if (CUSTOMERS_IN_BRANCH_REPORT_TYPES.has(reportTypeId)) {
    return {
      columns: CUSTOMERS_IN_BRANCH_COLUMNS,
      columnDefinitions: CUSTOMERS_IN_BRANCH_COLUMN_DEFINITIONS,
    };
  }
  return {};
};

const SelectedReportsTable = ({ reports, reportTypes, onDeleteReport, onUpdateReport }) => {
  const dispatch = useDispatch();
  const { subscription, level, activeReportTypeId } = useSelector((store) => store.subscriptionsReducer);
  const [showDropdown, setShowDropdown] = useState(false);

  // Get level name for filtering
  const levelName = useMemo(() => {
    return Object.keys(LEVELS)
      .find((key) => LEVELS[key] === level)
      ?.toLowerCase();
  }, [level]);

  // Get report range string
  const reportRangeStr = useMemo(() => {
    return {
      day: "daily",
      week: "weekly",
      month: "monthly",
    }[subscription.reportRange];
  }, [subscription.reportRange]);

  // Filter available report types
  const availableReportTypes = useMemo(() => {
    if (!levelName || !reportRangeStr) return [];
    
    return reportTypes.filter(
      (rt) =>
        rt.level === levelName &&
        rt.name.includes(reportRangeStr)
    );
  }, [reportTypes, levelName, reportRangeStr]);

  // Format options for Select
  const reportTypeOptions = useMemo(() => {
    return availableReportTypes
      .map((rt) => ({
        id: rt.id,
        name: formatReportTypeName(rt.name),
      }))
      .sort((left, right) => left.name.localeCompare(right.name));
  }, [availableReportTypes]);

  // Handle report type selection - auto-add to subscription
  const handleReportTypeSelect = (reportTypeId) => {
    if (!reportTypeId) return;
    const columnOptions = getReportColumnOptions(reportTypeId);
    const renderConfig = columnOptions.columns
      ? {
          ...DEFAULT_RENDER_CONFIG,
          charts: { enabled: !NO_CHART_AGENT_TICKETS_REPORT_TYPES.has(reportTypeId) },
          columns: columnOptions.columns,
        }
      : DEFAULT_RENDER_CONFIG;

    const newReport = {
      reportTypeId,
      listOrder: (subscription?.reports?.length || 0) + 1,
      renderConfig,
      exportConfig: { csv: false },
    };

    // Add branches array if it's a branch-level report
    if (level === LEVELS.BRANCH) {
      newReport.branches = [];
    }

    const updatedReports = [...(subscription?.reports || []), newReport];

    dispatch(
      updateLocalSubscription({
        ...subscription,
        reports: updatedReports,
      })
    );

    // Reset selection
    dispatch(setActiveReportTypeId(""));
    setShowDropdown(false);
  };

  return (
    <Box>
      <Box  m="60px -25px 30px"  w="auto" position='relative' borderColor="#ebebeb">
        <Separator />
        <AbsoluteCenter bg='white' px='4'>
          Report Types ({reports.length})
        </AbsoluteCenter>
      </Box>

          {
          !_.isEmpty(reports) ? (
          reports.map((report, i) => {
            const reportType = reportTypes.find(a => a.id === report.reportTypeId);
            const reportName = reportType?.name
              .split("_")
              .slice(1)
              .join("_")
              .replace(/_([a-z])/g, (match, letter) => " " + letter.toUpperCase()) // Add a space before the capital letter
              .replace(/^./, (str) => str.toUpperCase());
            const columnOptions = getReportColumnOptions(report.reportTypeId);
            const presentationStateItems = getMergeRenderConfigStateItems(report.renderConfig, {
              columnDefinitions: columnOptions.columnDefinitions,
            });
            const summaryItems = report.exportConfig?.csv
              ? [...presentationStateItems, "CSV"]
              : presentationStateItems;
            
            return (
                <HStack
                  key={i}
                  alignItems="start"
                  justifyContent="space-between"
                  borderBottom={i === reports.length - 1 ? "none" : "1px solid #ebebeb"}
                  p="10px 10px"
                  _hover={{ bg: "#ebebeb" }}
                >
                  <Box flex="1" minW={0}>
                    <HStack gap="10px" justifyContent="start" alignItems="flex-start">
                      <Text fontSize="15px" minW="18px" textAlign="right">
                        {i + 1}
                      </Text>
                      <Box minW={0}>
                        <Text fontSize="15px"> {reportName} </Text>
                        <Text mt="4px" fontSize="12px" color="gray.700" fontWeight="500" lineHeight="1.35">
                          Merge: {summaryItems.join(" | ")}
                        </Text>
                      </Box>
                    </HStack>
                  </Box>
                  <HStack alignItems="flex-start" justifyContent="space-between">
                    <Text cursor="pointer" _hover={{textDecoration: "underline"}} onClick={() => onUpdateReport(report.reportTypeId, i)}>Edit</Text>
                    <Text cursor="pointer" _hover={{textDecoration: "underline"}} onClick={() => onDeleteReport(i)}>Remove</Text>
                  </HStack>
                </HStack>
            );
          })
          ) : <Text>Add your first report with the buttons below</Text>}

      {/* Dropdown for selecting report type */}
      <Box id="inputToSelectReportType">
        {showDropdown && reportTypeOptions.length > 0 && (
          <Box mt="20px">
            <HStack justifyContent="flex-end" mb="6px">
              <IconButton
                aria-label="Close report type selection"
                title="Close report type selection"
                size="sm"
                bg="transparent"
                border="1px solid #d1d5db"
                borderRadius="4px"
                _hover={{ bg: "gray.100" }}
                onClick={() => {
                  setShowDropdown(false);
                  dispatch(setActiveReportTypeId(""));
                }}
              >
                <X size={16} />
              </IconButton>
            </HStack>
            <Select
              label="Report Type"
              placeholder="Select Report Type"
              value={activeReportTypeId}
              options={reportTypeOptions}
              onChange={(value) => handleReportTypeSelect(value)}
            />
          </Box>
        )}
      </Box>

      <Box>
        <Separator m="10px 0" borderColor="#ebebeb" />
        <HStack gap="8px" mt="20px">
          <Button
            flex="1"
            h="42px"
            justifyContent="center"
            gap="6px"
            background="var(--black-dry)"
            color="var(--white)"
            _hover={{ bg: "var(--black-2)", borderColor: "var(--black-2)" }}
            _disabled={{ opacity: 0.5, cursor: "not-allowed" }}
            borderRadius="0"
            border="1px solid var(--black)"
            fontWeight={400}
            fontSize="13px"
            disabled={showDropdown}
            onClick={() => {
              dispatch(updateLevel(LEVELS.ORGANIZATION));
              setShowDropdown(true);
            }}
          >
            <Icon boxSize="18px" color="#fff" as={IoAddSharp} />
            Organization
          </Button>
          <Button
            flex="1"
            h="42px"
            justifyContent="center"
            gap="6px"
            background="var(--black-dry)"
            color="var(--white)"
            _hover={{ bg: "var(--black-2)", borderColor: "var(--black-2)" }}
            _disabled={{ opacity: 0.5, cursor: "not-allowed" }}
            borderRadius="0"
            border="1px solid var(--black)"
            fontWeight={400}
            fontSize="13px"
            disabled={showDropdown}
            onClick={() => {
              dispatch(updateLevel(LEVELS.BRANCH));
              setShowDropdown(true);
            }}
          >
            <Icon boxSize="18px" color="#fff" as={IoAddSharp} />
            Branch
          </Button>
        </HStack>
      </Box>
    </Box>



  );
};

export default SelectedReportsTable;
